import { createTheme, TableColumn, TableStyles } from "react-data-table-component";
import { Equipment } from "../../models/equipment";
import moment from 'moment';

createTheme('custom', {
  text: {
    primary: '#ddd',
    secondary: '#ddd'
  },
  background: {
    default: 'transparent'
  },
  context: {
    background: '#cb4b16',
    text: '#FFFFFF',
  },
  divider: {
    default: '#444',
  },
  button: {
    default: '#2aa198',
    hover: 'rgba(0,0,0,.08)',
    focus: 'rgba(255,255,255,.12)',
    disabled: 'rgba(255, 255, 255, .34)',
  },
  sortFocus: {
    default: '#2aa198',
  },
  striped: {
    default: 'rgba(0,0,0,.05)'
  }
}, 'dark');

export const styles: TableStyles = {
  headRow: {
    style: {
      fontSize: '12px'
    }
  },
  rows: {
    style: {
      fontSize: '14px'
    }
  }
}

export const getColumns: (query: string) => TableColumn<Equipment>[] = (query: string) => {
  const queryRegex = new RegExp(query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'ig');
  const partRegex = new RegExp(query.split(/ +/).filter(x => x).map(x => x.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'ig').join('|'));
  
  const Highlight = ({value}: {value: any}) => {
    let text: string = value.toString();
    if (!query || text.toLowerCase().indexOf(query.toLowerCase()) === -1) {
      return <>{text}</>;
    } else {
      text = text.replace(queryRegex, (match: string) => '<span style="color: yellow;">' + match + '</span>');
      return <span dangerouslySetInnerHTML={{__html: text}}/>
    }
  }

  const HighlightMultiple = ({value}: {value: any}) => {
    let text: string = value.toString();
    if (!query) {
      return <>{text}</>;
    } else {
      text = text.replace(partRegex, (match: string) => '<span style="color: yellow;">' + match + '</span>');
      return <span dangerouslySetInnerHTML={{__html: text}}/>  
    }
  }

  return [
  {
    id: 'name',
    name: 'Name',
    selector: row => row.name,
    cell: row => <Highlight value={row.name}/>,
    sortable: true,
    sortFunction: (a, b) => a.name.replace(/^(?:an?|the) /, '').localeCompare(b.name.replace(/^(?:an?|the) /, '')),
    minWidth: '300px',
    wrap: true,
    grow: 0
  },
  {
    id: 'weaponType',
    name: 'Type',
    selector: row => `${row.weaponType} (${row.weaponHands}h)`,
    cell: row => <Highlight value={row.weaponType + ` (${row.weaponHands}h)`}/>,
    sortable: true,
    minWidth: '175px',
    wrap: true,
    grow: 0
  },
  {
    id: 'slot',
    name: 'Slot',
    selector: row => row.slot,
    cell: row => <Highlight value={row.slot}/>,
    sortable: true,
    minWidth: '100px',
    wrap: true,
    grow: 0
  },
  {
    id: 'ob',
    name: 'OB',
    selector: row => row.ob,
    cell: row => <Highlight value={row.ob}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'db',
    name: 'DB',
    selector: row => row.db,
    cell: row => <Highlight value={row.db}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'pb',
    name: 'PB',
    selector: row => row.pb,
    cell: row => <Highlight value={row.pb}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'def',
    name: 'DEF',
    selector: row => row.db + row.pb,
    cell: row => <Highlight value={row.db + row.pb}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'abs',
    name: 'ABS',
    selector: row => row.abs,
    cell: row => <Highlight value={row.abs}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'mvs',
    name: 'MVS',
    selector: row => row.mvs,
    cell: row => <Highlight value={row.mvs}/>,
    sortable: true,
    minWidth: '75px',
    wrap: true,
    grow: 0
  },
  {
    id: 'weight',
    name: 'Weight',
    selector: row => row.weight,
    cell: row => <Highlight value={row.weight}/>,
    sortable: true,
    minWidth: '100px',
    wrap: true,
    grow: 0
  },
  {
    id: 'damage',
    name: 'Damage',
    selector: row => `${row.damage} (${row.meanDamage})`,
    sortFunction: (a, b) => a.meanDamage - b.meanDamage,
    cell: row => <div><Highlight value={row.damage}/> <em className="text-muted"><Highlight value={`(${row.meanDamage})`}/></em></div>,
    sortable: true,
    minWidth: '100px',
    wrap: true,
    grow: 0
  },
  {
    id: 'bashRating',
    name: <div>Bash<br/>Rating</div>,
    selector: row => row.bashRating,
    cell: row => <Highlight value={row.bashRating}/>,
    sortable: true,
    minWidth: '85px',
    wrap: true,
    grow: 0
  },
  {
    id: 'tags',
    name: 'Tags',
    selector: row => row.setup,
    cell: row => {
      const arr = row.setup.replace(/ *, */g, ',').split(',').filter(x => x);
      return <div className="d-flex flex-wrap">
        {arr.map((x, i) => (
          <div key={i} className="me-3">
            <span className="text-muted">&bull; </span>
            <HighlightMultiple value={x.toLowerCase()}/>
          </div>
        ))}
      </div>;
    },
    sortable: true,
    minWidth: '200px',
    wrap: true,
    grow: 1
  },
  {
    id: 'updated',
    name: 'Updated',
    selector: row => moment(row.updated).format('M/DD/YY'),
    cell: row => <Highlight value={moment(row.updated).format('M/DD/YY')}/>,
    sortable: true,
    sortFunction: (a, b) => moment(a.updated).diff(b.updated),
    minWidth: '100px',
    wrap: true,
    grow: 0,
  }
];
};